// src/app/auth/store/loginSlice.js
import { createSlice } from '@reduxjs/toolkit';
import { showMessage } from 'app/store/fuse/messageSlice';
import UserLoginService from 'app/admin/user/service/UserLoginService';
import SamlLoginService from 'app/admin/user/service/SamlLoginService';
import { getUserData } from './userSlice';

const STEP = {
  LOGGED_IN: 'loggedIn',
  CHOOSE_PROJECT: 'chooseProject',
  CHOOSE_GROUP: 'chooseGroup',
  CHOOSE_ROLE: 'chooseRole',
  NO_PROJECT: 'noProject',
  NO_GROUP: 'noGroup',
  NO_ROLE: 'noRole'
};

const _getSearchParams = () => {
  const s = window.location.search || '';
  if (s) return new URLSearchParams(s);
  const h = window.location.hash || '';
  const i = h.indexOf('?');
  return i >= 0 ? new URLSearchParams(h.slice(i)) : new URLSearchParams('');
};

const _getLoginType = () => String(_getSearchParams().get('loginType') || '').toLowerCase();
const _getLoginService = () => (_getLoginType() === 'saml' ? SamlLoginService : UserLoginService);

const isStepSuccess = (result) => {
  if (!result) return false;
  if (typeof result.lastStepSuccessful === 'boolean') return result.lastStepSuccessful;
  if (typeof result.successful === 'boolean') return result.successful;
  return true;
};

const getNextStep = (result) => (result && result.nextStep != null ? String(result.nextStep) : null);

const _stepPayload = (result, nextStep) => ({
  nextStep,
  meta: result?.meta ?? null,
  client: result?.client ?? null,

  // root mezők, amiket a backend küldhet:
  projects: result?.projects ?? result?.client?.projects ?? null,
  projectId: result?.projectId ?? null,

  groups: result?.groups ?? null,
  userGroupId: result?.userGroupId ?? result?.groupId ?? null,

  roles: result?.roles ?? null,
  role: result?.role ?? null
});

const _finalizeLoggedIn = async (dispatch, result) => {
  dispatch(loginSuccess({ client: result?.client ?? null }));
  await dispatch(getUserData());
};

const _handleStep = (dispatch, step, result) => {
  if (!step) return false;

  if (step === STEP.NO_PROJECT) {
    dispatch(loginNextStep(_stepPayload(result, step)));
    dispatch(showMessage({ message: 'No project assigned. Please contact an administrator.' }));
    return true;
  }

  if (step === STEP.NO_GROUP) {
    dispatch(loginNextStep(_stepPayload(result, step)));
    dispatch(showMessage({ message: 'No group assigned. Please contact an administrator.' }));
    return true;
  }

  if (step === STEP.NO_ROLE) {
    dispatch(loginNextStep(_stepPayload(result, step)));
    dispatch(showMessage({ message: 'No role assigned. Please contact an administrator.' }));
    return true;
  }

  if (step === STEP.CHOOSE_PROJECT || step === STEP.CHOOSE_GROUP || step === STEP.CHOOSE_ROLE) {
    dispatch(loginNextStep(_stepPayload(result, step)));
    return true;
  }

  dispatch(loginNextStep(_stepPayload(result, step)));
  dispatch(showMessage({ message: `Unknown login step "${String(step)}". Please try again.` }));
  return true;
};

const _handleResult = async (dispatch, result, { onFail, failToast } = {}) => {
  const step = getNextStep(result);
  const loggedIn = result?.isLoggedIn === true || step === STEP.LOGGED_IN;

  if (loggedIn) {
    await _finalizeLoggedIn(dispatch, result);
    return true;
  }

  if (_handleStep(dispatch, step, result)) return true;

  if (!isStepSuccess(result)) {
    onFail?.();
    if (failToast) dispatch(showMessage({ message: failToast }));
    return true;
  }

  return false;
};

const _hasAnyKnownSignal = (result) => {
  if (!result) return false;
  const step = getNextStep(result);
  return result?.isLoggedIn === true || step != null || result?.client != null || result?.meta != null;
};

const _getLoginStateWithFallback = async () => {
  const t = _getLoginType();
  if (t === 'saml') return SamlLoginService.getLoginState();
  if (t) return UserLoginService.getLoginState();

  try {
    const r1 = await UserLoginService.getLoginState();
    if (_hasAnyKnownSignal(r1)) return r1;
  } catch (e) {}

  try {
    const r2 = await SamlLoginService.getLoginState();
    if (_hasAnyKnownSignal(r2)) return r2;
  } catch (e) {}

  return null;
};

export const checkLoginState = () => async (dispatch) => {
  dispatch(setBusy());
  try {
    const result = await _getLoginStateWithFallback();
    if (!result) {
      dispatch(resetLoginState());
      return;
    }

    const step = getNextStep(result);
    const loggedIn = result?.isLoggedIn === true || step === STEP.LOGGED_IN;

    if (loggedIn) {
      await _finalizeLoggedIn(dispatch, result);
      return;
    }

    if (_handleStep(dispatch, step, result)) return;

    dispatch(resetLoginState());
  } catch (e) {
    dispatch(resetLoginState());
  }
};

export const submitLogin = ({ email, password }) => async (dispatch) => {
  dispatch(loginStart());
  try {
    const result = await UserLoginService.login(email, password);
    const handled = await _handleResult(dispatch, result, {
      onFail: () => dispatch(loginError([{ type: 'email', message: 'Invalid email or password!' }])),
      failToast: 'Login failed. Please try again.'
    });
    if (handled) return;

    dispatch(loginError([{ type: 'server', message: 'Unexpected login state.' }]));
    dispatch(showMessage({ message: 'Unexpected login state. Please try again.' }));
  } catch (e) {
    dispatch(loginError([{ type: 'server', message: 'Network error occurred.' }]));
    dispatch(showMessage({ message: 'Network error. Please try again.' }));
  }
};

export const submitProjectChoice = (projectId) => async (dispatch) => {
  dispatch(setBusy());
  try {
    const service = _getLoginService();
    const result =
      service && typeof service.setProjectId === 'function'
        ? await service.setProjectId(projectId)
        : await UserLoginService.setProjectId(projectId);

    const handled = await _handleResult(dispatch, result, {
      onFail: () => dispatch(loginStepError([{ type: 'server', message: 'Project selection failed.' }])),
      failToast: 'Project selection failed. Please try again.'
    });
    if (handled) return;

    dispatch(loginSuccess({ client: result?.client ?? null }));
    await dispatch(getUserData());
  } catch (e) {
    dispatch(loginStepError([{ type: 'server', message: 'Network error occurred.' }]));
    dispatch(showMessage({ message: 'Network error. Please try again.' }));
  }
};

export const submitGroupChoice = (groupId) => async (dispatch) => {
  dispatch(setBusy());
  try {
    const service = _getLoginService();
    const result =
      service && typeof service.setUserGroupId === 'function'
        ? await service.setUserGroupId(groupId)
        : await UserLoginService.setUserGroupId(groupId);

    const handled = await _handleResult(dispatch, result, {
      onFail: () => dispatch(loginStepError([{ type: 'server', message: 'Group selection failed.' }])),
      failToast: 'Group selection failed. Please try again.'
    });
    if (handled) return;

    dispatch(loginSuccess({ client: result?.client ?? null }));
    await dispatch(getUserData());
  } catch (e) {
    dispatch(loginStepError([{ type: 'server', message: 'Network error occurred.' }]));
    dispatch(showMessage({ message: 'Network error. Please try again.' }));
  }
};

export const submitRoleChoice = (role) => async (dispatch) => {
  dispatch(setBusy());
  try {
    const service = _getLoginService();
    const result =
      service && typeof service.setRole === 'function'
        ? await service.setRole(role)
        : await UserLoginService.setRole(role);

    const handled = await _handleResult(dispatch, result, {
      onFail: () => dispatch(loginStepError([{ type: 'server', message: 'Role selection failed.' }])),
      failToast: 'Role selection failed. Please try again.'
    });
    if (handled) return;

    dispatch(loginSuccess({ client: result?.client ?? null }));
    await dispatch(getUserData());
  } catch (e) {
    dispatch(loginStepError([{ type: 'server', message: 'Network error occurred.' }]));
    dispatch(showMessage({ message: 'Network error. Please try again.' }));
  }
};

const initialState = {
  success: false,
  errors: [],
  inProgress: false,
  isLoggedIn: false,
  nextStep: null,
  meta: null,
  client: null,

  // root mezők (választó step-ekhez)
  projects: null,
  projectId: null,
  groups: null,
  userGroupId: null,
  roles: null,
  role: null
};

const loginSlice = createSlice({
  name: 'auth/login',
  initialState,
  reducers: {
    loginStart: (state) => {
      state.inProgress = true;
      state.errors = [];
      state.nextStep = null;
      state.meta = null;
      state.client = null;
      state.success = false;
      state.isLoggedIn = false;

      state.projects = null;
      state.projectId = null;
      state.groups = null;
      state.userGroupId = null;
      state.roles = null;
      state.role = null;
    },
    setBusy: (state) => {
      state.inProgress = true;
    },
    loginSuccess: (state, action) => {
      state.inProgress = false;
      state.success = true;
      state.isLoggedIn = true;
      state.errors = [];
      state.nextStep = null;
      state.meta = null;
      state.client = action?.payload?.client ?? null;

      state.projects = null;
      state.projectId = null;
      state.groups = null;
      state.userGroupId = null;
      state.roles = null;
      state.role = null;
    },
    loginNextStep: (state, action) => {
      const {
        nextStep,
        meta = null,
        client = null,
        projects = null,
        projectId = null,
        groups = null,
        userGroupId = null,
        roles = null,
        role = null
      } = action.payload || {};

      state.inProgress = false;
      state.success = false;
      state.isLoggedIn = false;
      state.errors = [];

      state.nextStep = nextStep || null;
      state.meta = meta;
      state.client = client;

      state.projects = projects;
      state.projectId = projectId;
      state.groups = groups;
      state.userGroupId = userGroupId;
      state.roles = roles;
      state.role = role;
    },
    loginError: (state, action) => {
      state.inProgress = false;
      state.success = false;
      state.isLoggedIn = false;
      state.errors = Array.isArray(action.payload) ? action.payload : [];
      state.nextStep = null;
      state.meta = null;
      state.client = null;

      state.projects = null;
      state.projectId = null;
      state.groups = null;
      state.userGroupId = null;
      state.roles = null;
      state.role = null;
    },
    loginStepError: (state, action) => {
      state.inProgress = false;
      state.success = false;
      state.isLoggedIn = false;
      state.errors = Array.isArray(action.payload) ? action.payload : [];
    },
    resetLoginState: (state) => {
      Object.assign(state, initialState);
    }
  },
  extraReducers: {}
});

export const { loginStart, setBusy, loginSuccess, loginNextStep, loginError, loginStepError, resetLoginState } = loginSlice.actions;
export default loginSlice.reducer;
